import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { MoreVert } from '@mui/icons-material';
import HdIcon from '@mui/icons-material/Hd';
import Button from '@mui/material/Button';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useNavigate } from '@tanstack/react-router';
import type React from 'react';
import { useContext, useState } from 'react';
import assetsByCollectionIdPlaylist from 'src/api/collection/assetsByCollectionIdPlaylist';
import editCollection from 'src/api/collection/editCollection';
import { allConfigs } from 'src/api/configurations';
import { StyledContextMenu } from 'src/components/menus/StyledContextMenu';
import type { ActionItem } from 'src/constants';
import { ACTIONS, ASSET, HOME_PATH, SELECTED_ITEMS } from 'src/constants';
import CreatePlayListContext from 'src/context/CreatePlayListContext';
import { insertArrayAfter } from 'src/helpers/actionMenuHelper';
import { applyPermissions } from 'src/helpers/permissionsHelper';
import viewStateVar from 'src/store/viewState';
import type { ActionOption, AssetPermissions } from '../types';

const useStyles = makeStyles((theme: Theme) => ({
  cardMoreActionsButton: {
    right: '10px',
    top: '10px',
    cursor: 'pointer',
    '& svg': {
      color: theme.palette.text.primary,
      padding: '2px',

      borderRadius: '4px',
    },
  },
  button: {
    minWidth: 'auto',
  },
}));

interface InlineCardActionsProps {
  itemID: string;
  permissions?: AssetPermissions;
  disable?: boolean;
}

interface ConfigNode {
  optionValue?: string;
}

interface ConfigEdge {
  node: ConfigNode;
}

interface AllConfigsData {
  allConfigurations?: {
    edges?: ConfigEdge[];
  };
}

const InlineCardActions: React.FC<InlineCardActionsProps> = ({ itemID, permissions, disable }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const viewState = useReactiveVar(viewStateVar);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { setCreateAddedAssets } = useContext(CreatePlayListContext);

  const { data: hiresActionMenuItem } = useQuery<AllConfigsData>(allConfigs, {
    variables: {
      optionScope: 'action-menu',
      optionKey: 'hires_action_menu_item',
    },
    fetchPolicy: 'network-only',
    onCompleted: () => {
      // NOTE: keep these comments, see lead developer before removing
      let updatedActions = insertArrayAfter(
        actions,
        [],
        'Share' as unknown as ActionOption,
      ) as unknown as ActionOption[];
      const hiresActionValue = hiresActionMenuItem?.allConfigurations?.edges?.[0]?.node?.optionValue;
      if (permissions)
        updatedActions = applyPermissions(
          updatedActions as unknown as Parameters<typeof applyPermissions>[0],
          permissions,
          hiresActionValue ?? null,
        ) as unknown as ActionOption[];
      setOptions(updatedActions);
    },
  });

  const hiresActionValue = hiresActionMenuItem?.allConfigurations?.edges?.[0]?.node?.optionValue;

  const actions: ActionOption[] = [
    ...ACTIONS.slice(0, 2),
    { label: hiresActionValue || '', icon: <HdIcon sx={{ color: 'text.primary' }} /> },
    ...ACTIONS.slice(2),
  ];

  const [options, setOptions] = useState<ActionOption[]>(ACTIONS);

  const [addItemHandler] = useMutation(editCollection, {
    fetchPolicy: 'network-only',
    refetchQueries: [assetsByCollectionIdPlaylist],
  });

  const addItemCollectionHandler = (selectedAssetForAdding: string): void => {
    addItemHandler({
      variables: {
        collectionId: viewState.selectedCollectionId,
        assetIds: selectedAssetForAdding,
        collectionType: 'PLAYLIST',
        autoSeq: true,
      },
    });
  };

  const handleAction = (action: ActionItem): void => {
    const optionLabel = action.label?.toLowerCase();

    if (optionLabel === 'share') {
      viewStateVar({
        ...viewStateVar(),
        shareDialog: itemID,
        shareContent: ASSET,
      });
    } else if (optionLabel === 'download') {
      viewStateVar({
        ...viewStateVar(),
        libraryDownloadDialog: itemID,
      });
    } else if (optionLabel === hiresActionValue?.toLowerCase()) {
      viewStateVar({
        ...viewStateVar(),
        hiresDialog: SELECTED_ITEMS,
        selectedAssets: [...viewStateVar().selectedAssets, itemID],
      });
    } else if (optionLabel === 'delete') {
      viewStateVar({
        ...viewStateVar(),
        deleteDialog: itemID,
        selectedAssets: [...viewStateVar().selectedAssets, itemID],
      });
    } else if (optionLabel === 'add to playlist') {
      if (!viewState.openEditCollection) {
        setCreateAddedAssets((ids) => [
          ...ids,
          { id: itemID } as import('src/context/CreatePlayListContext').PlaylistAsset,
        ]);
        viewStateVar({
          ...viewStateVar(),
          showRightSidebar: true,
          openNewCollection: true,
          showHorizontal: viewStateVar().showRightSidebar && false,
          showLeftSidebar: viewStateVar().showRightSidebar && false,
        });
        if (viewState.showRightSidebar) {
          viewStateVar({
            ...viewStateVar(),
            rightDrawerWidth: viewState.defaultRightDrawerWidth,
          });
        } else {
          viewStateVar({
            ...viewStateVar(),
            rightDrawerWidth: viewState.minRightDrawerWidth,
          });
        }
      }
      if (viewState.openEditCollection) {
        addItemCollectionHandler(itemID);
      }
    } else if (optionLabel === 'edit') {
      viewStateVar({
        ...viewStateVar(),
        editClip: itemID,
      });
      // redirect to media-page if user isn't there already
      if (!window.location.pathname.includes(`media-page/`)) {
        navigate({ to: `${HOME_PATH}/media-page/${itemID}` });
      }
    }
    setAnchorEl(null);
  };

  const handleToggle = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
    viewStateVar({
      ...viewState,
      selectedAssets: [],
    });
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  return (
    <div className={classes.cardMoreActionsButton}>
      <Button
        variant="text"
        color="primary"
        size="small"
        aria-controls={open ? 'split-button-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-label="select merge strategy"
        aria-haspopup="menu"
        classes={{ root: classes.button }}
        disabled={disable}
        onClick={handleToggle}
      >
        <MoreVert />
      </Button>
      <StyledContextMenu
        actions={options as ActionItem[]}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onAction={handleAction}
        placement="bottom-end"
        testIdPrefix="cyp-mc-action"
        id="split-button-menu"
      />
    </div>
  );
};

export default InlineCardActions;
